<template>
  <HeroSection />

  <div class="home">
    <section id="pitch" class="home__pitch">
      <h2 class="home__title">{{ t('home.pitchTitle') }}</h2>
      <Prose center>
        <p>{{ t('home.pitchBody') }}</p>
      </Prose>
    </section>

    <!--
      Four bordered cards on a dark page read as a control panel, not as a
      pitch. They are hairline-ruled columns now: the same four ideas with the
      box removed, which is most of where the landing's air came from.
    -->
    <section class="home__beats" :aria-label="t('home.pitchTitle')">
      <ul class="beats">
        <li v-for="key in beatKeys" :key="key" class="beat">
          <h3 class="beat__title">{{ t(`home.beats.${key}.title`) }}</h3>
          <p class="beat__body">{{ t(`home.beats.${key}.body`) }}</p>
        </li>
      </ul>
    </section>

    <section class="home__world">
      <div class="home__world-text">
        <h2 class="home__title home__title--left">{{ t('home.worldTitle') }}</h2>
        <Prose>
          <p>{{ t('home.worldBody') }}</p>
        </Prose>
        <LandingButton variant="ghost" :to="{ name: 'WorldHub' }">{{ t('home.worldCta') }}</LandingButton>
      </div>
      <SmartImage class="home__world-art" :src="worldArt" alt="" ratio="4 / 3" />
    </section>

    <section class="home__start">
      <h2 class="home__title">{{ t('home.startTitle') }}</h2>
      <StepList class="home__steps" :steps="startSteps" />
      <p class="home__note">{{ t('home.startNote') }}</p>
      <div class="home__cta">
        <LandingButton variant="accent" :to="ctaTarget">{{ ctaLabel }}</LandingButton>
        <LandingButton variant="ghost" :to="{ name: 'Community' }">{{ t('community.joinDiscord') }}</LandingButton>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import HeroSection from '@/components/HeroSection.vue';
import { usePrimaryCta } from '@/composables/usePrimaryCta.js';
import Prose from '@/components/public/Prose.vue';
import StepList from '@/components/public/StepList.vue';
import SmartImage from '@/components/public/SmartImage.vue';
import LandingButton from '@/components/btn/LandingButton.vue';
/*
 * Kept deliberately dark: an earlier bright-pastel render of this subject was
 * the lightest thing on a near-black page and pulled the eye straight off the
 * heading beside it. dx-4 now carries the hero's palette, so the section reads
 * as part of the page rather than a window cut into it.
 */
import worldArt from '@/assets/images/dx-4.webp';

const { t } = useI18n();
const { ctaTarget, ctaLabel } = usePrimaryCta();

const beatKeys = ['gm', 'character', 'world', 'together'];

const startSteps = computed(() =>
  ['one', 'two', 'three'].map((key) => ({ text: t(`home.startSteps.${key}`) }))
);
</script>

<style scoped>
.home {
  position: relative;
  z-index: var(--z-content);
  width: 100%;
  max-width: var(--page-max-wide);
  margin: 0 auto;
  /* The hero's fade already lands on --surface-0, so this is breathing room
   * rather than a seam to hide: the first heading gets a run-up instead of
   * starting the instant the picture ends. */
  padding: var(--rhythm-block) var(--page-gutter) var(--rhythm-section);
  display: flex;
  flex-direction: column;
  /* The section rhythm is now visibly larger than the gaps inside a section,
   * which is what makes each block read as one idea instead of the page
   * reading as one long column of everything. */
  gap: var(--rhythm-section);
}

.home__title {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-display);
  line-height: var(--leading-tight);
  text-align: center;
  text-wrap: balance;
  margin: 0 0 var(--space-5);
}

.home__title--left {
  text-align: left;
}

.home__pitch {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Anchor target for the hero's scroll cue; clears the sticky header. */
  scroll-margin-top: var(--header-h);
}

/* A centred heading over a left-ragged block leaves the two optical centres
 * ~160px apart, which is what made this section look like two unrelated
 * pieces. The pitch is three lines — short enough to centre with it. */
.home__pitch :deep(.prose) {
  text-align: center;
}

.beats {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(14rem, 100%), 1fr));
  gap: var(--rhythm-block) var(--space-7);
}

.beat {
  margin: 0;
  padding-top: var(--space-4);
  border-top: 1px solid var(--border);
}

.beat__title {
  font-family: var(--font-body);
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  letter-spacing: 0;
  /* Was --accent on all four at once. Accent now means "the one action". */
  color: var(--text);
  margin: 0 0 var(--space-2);
}

.beat__body {
  color: var(--text-muted);
  line-height: var(--leading-body);
  margin: 0;
}

.home__world {
  display: grid;
  gap: var(--space-7);
  align-items: center;
}

.home__world-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-4);
}

.home__start {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.home__steps {
  width: 100%;
  max-width: var(--measure);
}

.home__note {
  margin: var(--space-6) 0 0;
  color: var(--text-faint);
  font-size: var(--text-sm);
  text-align: center;
  max-width: var(--measure-narrow);
}

.home__cta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-3);
  margin-top: var(--space-5);
}

@media (min-width: 60rem) {
  .home__world {
    grid-template-columns: 1fr 1fr;
    gap: var(--space-9);
  }
}
</style>
